import { useLingui } from '@lingui/react/macro';
import { type FC, useMemo } from 'react';
import { useDirection } from '../i18n';
import { useCoin } from './api';
import { abbreviate, addComma, fixShape, toDigits } from './number';
import { cn } from './react';

export type DigitsProps = {
  value?: string | number | null;
  coin?: string;
  unit?: string;
  color?: 'auto' | 'negative' | 'positive' | 'inherit';
  mask?: boolean;
  abbreviate?: boolean;
  sign?: 'always' | 'negative' | 'never';
  className?: string;
};

const useLabel = ({ unit, coin }: { unit?: string; coin?: string }) => {
  const { i18n } = useLingui();
  const coinObject = useCoin(coin?.toUpperCase());
  const direction = useDirection();
  const coinName =
    i18n.locale !== 'en'
      ? coinObject.data?.iraqi_name
      : coinObject.data?.symbol;

  return {
    prefix: direction === 'rtl' ? unit : undefined,
    suffix: direction === 'rtl' ? coinName : (coinName ?? unit),
  };
};

export const Digits: FC<DigitsProps> = ({
  className,
  unit,
  coin,
  mask,
  value,
  abbreviate: abbreviateProp,
  color = 'inherit',
  sign = 'negative',
}) => {
  const { prefix, suffix } = useLabel({ unit, coin });
  const isUndef = typeof value === 'undefined' || value === null;

  const [positiveBase, decimal] = useMemo(() => {
    if (mask) return ['*******', ''];
    if (isUndef) return coin ? ['----', '--'] : ['---', ''];
    let ret = value.toString();
    ret = ret.startsWith('-') ? ret.slice(1) : ret;
    ret = toDigits(ret);
    ret = fixShape(ret);
    if (abbreviateProp) {
      ret = abbreviate(ret);
    }
    ret = addComma(ret);
    return ret.split('.');
  }, [mask, value, isUndef, abbreviateProp, coin]);

  const side = +(value ?? '') > 0 ? 1 : +(value ?? '') < 0 ? -1 : 0;

  return (
    <data
      value={!mask ? value?.toString() : undefined}
      className={cn(
        (color === 'positive' || (color === 'auto' && side > 0)) &&
          'text-content-positive',
        (color === 'negative' || (color === 'auto' && side < 0)) &&
          'text-content-negative',
        className,
      )}
    >
      {prefix && !isUndef && !mask && (
        <span className={cn(prefix.length > 1 ? 'me-1 opacity-70' : 'me-px')}>
          {prefix}
        </span>
      )}
      <span dir="ltr">
        {sign !== 'never' && (sign === 'always' || side < 0) && (
          <span>{side > 0 ? '+' : side < 0 ? '-' : ''}</span>
        )}
        <span className={cn(isUndef && 'opacity-50')}>{positiveBase}</span>
        {decimal && !isUndef && <span>.{decimal ?? ''}</span>}
      </span>
      {suffix && !isUndef && !mask && (
        <span className={cn(suffix.length > 1 ? 'ms-1' : 'ms-px')}>
          {suffix}
        </span>
      )}
    </data>
  );
};
